import {getTestProps} from '@benchling/js-test-utils';
import {ActionIcon} from '@mantine/core';
import React, {useMemo} from 'react';

import SkillActionsMenu from 'llm/chat-library/skills/SkillActionsMenu';
import {
  buildSkillOverflowMenuSections,
  useSkillOverflowMenuHandlers,
} from 'llm/chat-library/skills/SkillOverflowMenuContext';
import type {SkillSummary} from 'llm/chat-library/skills/SkillsDAO';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import MantineDropdownMenu from 'ui-library/menus/MantineDropdownMenu';

interface SkillOverflowMenuProps {
  skill: SkillSummary;
  /**
   * Component name used for test props, so that consumers (list vs. detail) keep
   * distinct, stable test ids while sharing a single menu implementation.
   */
  testComponentName: string;
  onArchive: (skillId: string, reason: string) => void;
  /** Opens the skill's detail view (if needed) and activates its Manage access tab. */
  onManageAccess: (skill: SkillSummary) => void;
  /**
   * The historical version the surrounding view is displaying. When absent, the download action
   * fetches the skill's current version.
   */
  viewingVersionId?: string;
}

/**
 * Overflow menu for a skill. Uses {@link MantineDropdownMenu} when redesign handlers are provided
 * via context; otherwise falls back to the legacy {@link SkillActionsMenu}.
 */
export default function SkillOverflowMenu({
  skill,
  testComponentName,
  onArchive,
  onManageAccess,
  viewingVersionId,
}: SkillOverflowMenuProps): JSX.Element {
  const redesignHandlers = useSkillOverflowMenuHandlers();

  const sections = useMemo(() => {
    if (redesignHandlers == null) {
      return null;
    }
    return buildSkillOverflowMenuSections(
      skill,
      {...redesignHandlers, onManageAccess},
      {testComponentName, viewingVersionId}
    );
  }, [redesignHandlers, onManageAccess, skill, testComponentName, viewingVersionId]);

  if (sections == null) {
    return (
      <SkillActionsMenu
        skill={skill}
        testComponentName={testComponentName}
        onArchive={onArchive}
        onManageAccess={onManageAccess}
        viewingVersionId={viewingVersionId}
      />
    );
  }

  return (
    <MantineDropdownMenu
      sections={sections}
      position="bottom-end"
      trigger={
        <ActionIcon
          size="sm"
          variant="subtle"
          aria-label="More options"
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
          }}
          {...getTestProps({component: testComponentName, element: 'moreOptionsButton'})}
        >
          <BenchlingIcon icon="ellipsis-vertical" />
        </ActionIcon>
      }
    />
  );
}
